import { Source, SourceStatus } from '@wove/api/types'
import { format } from 'date-fns'
import { useDeleteSourceMutation, useRestApi, ExportOption } from '@wove/api'
import { useAuth } from '@src/hooks/useAuth'
//New
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  TableCell,
  TableRow,
  Button,
  cn,
} from '@wove/web-ui'
import { FileText, X, AlertCircle, MoreHorizontal, Download } from 'lucide-react'
import { formatFileSize, exportData } from '@src/util'

interface SourceRowProps {
  file: Source
  isNew?: boolean
}

const SourceRow = ({ file, isNew }: SourceRowProps) => {
  const [, executeDelete] = useDeleteSourceMutation()

  const restApi = useRestApi()
  const { user, customer } = useAuth()

  if (!user || !customer) {
    return null
  }

  const exportOptions: ExportOption[] = [{ label: 'Export original', format: 'original' }]

  const allExportOptions: ExportOption[] = [
    { label: 'Export data to GPM', format: 'gpm' },
    { label: 'Export data to Winmore (GPM)', format: 'winmore-gpm' },
    { label: 'Export data to Explorate', format: 'explorate' },
  ]

  const exportFormats = customer?.rateIngestion?.exportFormats || []
  const isAllExport = exportFormats.length === 1 && exportFormats[0] === 'all'

  if (isAllExport) {
    exportOptions.push(...allExportOptions)
  } else {
    exportOptions.push(
      ...allExportOptions.filter((option) => exportFormats.includes(option.format)),
    )
  }

  return (
    <TableRow
      key={file.id}
      className={cn(
        isNew && 'animate-in slide-in-from-top-2 fade-in duration-300 bg-blue-50/50',
        file.status === SourceStatus.Failed && 'bg-red-50/30',
      )}
    >
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="truncate max-w-[180px] sm:max-w-[250px] md:max-w-[350px]">
            {file.name}
          </span>
          {file.status === SourceStatus.Failed && (
            <Button
              variant="ghost"
              size="icon"
              className="h-5 w-5 p-0"
              // onClick={() => showErrorDetails(file)}
            >
              <AlertCircle className="h-4 w-4 text-red-500" />
            </Button>
          )}
        </div>
      </TableCell>
      <TableCell>{typeof file.size === 'number' ? formatFileSize(file.size) : ''}</TableCell>
      <TableCell>
        {file.status === SourceStatus.Complete ? (
          <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-green-100 text-green-700">
            Complete
          </div>
        ) : file.status === SourceStatus.Failed ? (
          <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-red-100 text-red-700">
            <AlertCircle className="mr-1 h-3 w-3" />
            Error
          </div>
        ) : (
          <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-yellow-100 text-yellow-700">
            Processing
          </div>
        )}
      </TableCell>
      <TableCell>{format(new Date(file.createdAt), 'MMM do, yyyy')}</TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            {file.status === SourceStatus.Complete && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="flex w-full items-center">
                  <Download className="mr-2 h-4 w-4" />
                  Export
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {exportOptions.map((option) => (
                    <DropdownMenuItem
                      onClick={() => exportData(restApi, file.id, option.format)}
                      key={option.format}
                    >
                      {option.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            {/* {file.status === SourceStatus && (
                              <DropdownMenuItem onClick={() => showErrorDetails(file)}>
                                <AlertCircle className="mr-2 h-4 w-4" />
                                View Issue
                            </DropdownMenuItem>
                          )} */}
            {/* {file.status === SourceStatus.Error && (
                            <DropdownMenuItem onClick={() => retryProcessing(file.id)}>
                              <RefreshCcw className="mr-2 h-4 w-4" />
                              Retry Processing
                            </DropdownMenuItem>
                          )} */}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => executeDelete({ deleteSourceId: file.id })}
              className="text-red-600"
            >
              <X className="mr-2 h-4 w-4" />
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  )
}

export default SourceRow
